import { useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Upload, FileText, X, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import SLABadge from "../components/SLABadge";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

export default function SkyworthUpload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [jobCards, setJobCards] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadJobCards = useCallback(async () => {
    try {
      const { data } = await api.get("/job-cards?status=NEW");
      setJobCards(data.items || []);
    } catch { /* ignore */ }
    setLoaded(true);
  }, []);

  if (!loaded) loadJobCards();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (dropped.length === 0) {
      toast.error("Please drop PDF files only");
      return;
    }
    setFiles((prev) => [...prev, ...dropped]);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selected]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        await api.post("/job-cards", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        success++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (success > 0) {
      toast.success(`${success} job card(s) created`);
      setFiles([]);
      loadJobCards();
    }
    setUploading(false);
  };

  return (
    <div data-testid="skyworth-upload-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <Upload size={24} className="text-teal-600" /> Upload Job Cards
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Drag & drop PDF files to create new job cards. Filenames become Job Card Numbers.
        </p>
      </div>

      {/* Drop Zone */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`drag-zone ${dragOver ? "drag-over" : ""} rounded-xl p-12 text-center cursor-pointer`}
        onClick={() => document.getElementById("file-input").click()}
      >
        <Upload size={40} className="mx-auto text-slate-400 mb-4" />
        <p className="text-slate-700 font-medium">Drop PDF files here or click to browse</p>
        <p className="text-slate-400 text-xs mt-1">Only PDF files accepted</p>
        <input
          id="file-input"
          data-testid="file-input"
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Staged Files */}
      {files.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            {files.length} file(s) ready to upload
          </p>
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl">
              <FileText size={16} className="text-teal-600" />
              <span className="text-sm text-slate-800 flex-1 truncate">{f.name}</span>
              <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(1)} KB</span>
              <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-400">
                <X size={14} />
              </button>
            </div>
          ))}
          <Button
            data-testid="upload-submit-btn"
            onClick={uploadFiles}
            disabled={uploading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {uploading ? "Uploading..." : `Upload ${files.length} File(s)`}
          </Button>
        </div>
      )}

      {/* Upload History */}
      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-3">
          Upload History
        </h2>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 text-[10px] uppercase tracking-wider">
                <th className="text-left p-3">Job Card #</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">SLA</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {jobCards.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400">No job cards yet</td></tr>
              )}
              {jobCards.map((jc) => (
                <tr key={jc.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-800">{jc.job_card_number}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                      {jc.status}
                    </Badge>
                  </td>
                  <td className="p-3"><SLABadge sla={jc.sla} /></td>
                  <td className="p-3 text-slate-500 text-xs">{formatDate(jc.date_created)}</td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`view-job-${jc.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/jobs/${jc.id}`)}
                      className="text-teal-600 hover:text-teal-500 text-xs"
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
